import axios from 'axios'
import VxeUI from 'vxe-pc-ui'

// 存储正在请求的列表，用于取消重复请求
const pendingRequests = new Map()

// 生成请求的唯一标识
const getRequestKey = (config) => {
  const { url, method, params, data } = config
  return [url, method, JSON.stringify(params), JSON.stringify(data)].join('&')
}

// 添加请求到 pending
const addPendingRequest = (config) => {
  const key = getRequestKey(config)
  config.cancelToken = config.cancelToken || new axios.CancelToken((cancel) => {
    if (!pendingRequests.has(key)) {
      pendingRequests.set(key, cancel)
    }
  })
}

// 移除请求从 pending
const removePendingRequest = (config) => {
  const key = getRequestKey(config)
  if (pendingRequests.has(key)) {
    const cancel = pendingRequests.get(key)
    cancel()
    pendingRequests.delete(key)
  }
}

// 清除所有 pending 请求
export const clearPendingRequests = () => {
  pendingRequests.forEach((cancel) => {
    cancel()
  })
  pendingRequests.clear()
}

// 获取环境变量
const getBaseURL = () => {
  // Vue CLI build 时 NODE_ENV = 'production'
  if (process.env.NODE_ENV === 'production') {
    return 'api'
  }
  return 'http://localhost:5160/api'
}

// 创建 axios 实例
const service = axios.create({
  baseURL: getBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json;charset=utf-8',
  },
  
})

// 请求拦截器
service.interceptors.request.use(
  config => {
    // 取消重复请求（如果是同一个请求，取消之前的）
    removePendingRequest(config)
    addPendingRequest(config)
    
    // 添加 token
    const token = localStorage.getItem('token')
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    
    // 添加语言等通用参数
    const language = localStorage.getItem('language') || 'zh-CN'
    config.headers['Accept-Language'] = language
    
    // 打印请求信息（仅开发环境）
    if (import.meta.env.MODE === 'development') {
      console.log('🚀 请求:', config.method.toUpperCase(), config.baseURL + config.url, config.params || config.data || '')
    }
    
    // 如果配置了 loading，显示 loading
    if (config.showLoading) {
      config.loadingInstance = VxeUI.loading({
        target: document.body,
        text: config.loadingText || '加载中...',
        background: 'rgba(0, 0, 0, 0.7)',
        icon: 'vxe-icon-loading'
      })
    }
    
    return config
  },
  error => {
    console.error('❌ 请求错误:', error)
    return Promise.reject(error)
  }
)

// 响应拦截器
service.interceptors.response.use(
  response => {
    // 移除 pending 请求
    removePendingRequest(response.config)
    
    // 关闭 loading
    if (response.config.showLoading && response.config.loadingInstance) {
      response.config.loadingInstance.close()
    }
    
    // 打印响应信息（仅开发环境）
    if (import.meta.env.MODE === 'development') {
      console.log('✅ 响应:', response.config.url, response.data)
    }
    
    const res = response.data
    
    // 处理下载文件
    if (response.config.responseType === 'blob') {
      return res
    }
    
    // 根据后端返回码处理
    if (res.code !== undefined && res.code !== 200) {
      // 不自动显示错误信息的情况
      if (!response.config.hideError) {
        VxeUI.modal.message({
          message: res.message || '请求失败',
          status: 'error'
        })
      }
      
      // token 过期处理
      if (res.code === 401) {
        localStorage.removeItem('token')
        VxeUI.modal.message({
          message: '登录已过期，请重新登录',
          status: 'warning'
        })
        // 跳转到登录页
        setTimeout(() => {
          window.location.href = '/login'
        }, 1500)
      }
      
      return Promise.reject(new Error(res.message || 'Error'))
    }
    
    return res
  },
  error => {
    // 移除 pending 请求
    if (error.config) {
      removePendingRequest(error.config)
      
      // 关闭 loading
      if (error.config.showLoading && error.config.loadingInstance) {
        error.config.loadingInstance.close()
      }
    }
    
    // 处理取消的请求
    if (axios.isCancel(error)) {
      console.log('⛔ 请求已取消:', error.message)
      return Promise.reject(error)
    }
    
    // 错误处理
    let errorMessage = ''
    if (error.response) {
      const status = error.response.status
      switch (status) {
        case 400:
          errorMessage = '请求参数错误'
          break
        case 401:
          errorMessage = '未授权，请重新登录'
          localStorage.removeItem('token')
          setTimeout(() => {
            window.location.href = '/login'
          }, 1500)
          break
        case 403:
          errorMessage = '拒绝访问'
          break
        case 404:
          errorMessage = '请求资源不存在'
          break
        case 500:
          errorMessage = '服务器内部错误'
          break
        case 502:
          errorMessage = '网关错误'
          break
        case 503:
          errorMessage = '服务不可用'
          break
        case 504:
          errorMessage = '网关超时'
          break
        default:
          errorMessage = error.message || '请求失败'
      }
    } else if (error.code === 'ECONNABORTED') {
      errorMessage = '请求超时'
    } else if (error.message === 'Network Error') {
      errorMessage = '网络异常，请检查网络连接'
    } else {
      errorMessage = error.message || '请求失败'
    }
    
    // 打印错误信息（仅开发环境）
    if (import.meta.env.MODE === 'development') {
      console.error('❌ 响应错误:', errorMessage, error)
    }
    
    // 不自动显示错误信息的情况
    if (!error.config || !error.config.hideError) {
      VxeUI.modal.message({
        message: errorMessage,
        status: 'error'
      })
    }
    
    return Promise.reject(error)
  }
)

export default service